import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import { PRIORITY_LEVELS } from "../../../constants/inventory";
import { IoArrowBack } from "react-icons/io5";
import { MdAdd, MdDelete } from "react-icons/md";
import { Skeleton } from "@mui/material";

const EditRequisition = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const { id } = useParams();

  const [items, setItems] = useState([{ item: "", qty: 1, purpose: "" }]);
  const [priority, setPriority] = useState("medium");
  const [project, setProject] = useState("");
  const [department, setDepartment] = useState("");
  const [notes, setNotes] = useState("");

  const [inventoryItems, setInventoryItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [reqRes, itemsRes, projectsRes, deptsRes] = await Promise.all([
        fetch(`/api/getRequisition/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch("/api/getAllInventoryItems?limit=100", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch("/api/getAllProjects?limit=100", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch("/api/getAllDepartments", {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const reqData = await reqRes.json();
      const itemsData = await itemsRes.json();
      const projectsData = await projectsRes.json();
      const deptsData = await deptsRes.json();

      if (itemsRes.ok) setInventoryItems(itemsData.items || []);
      if (projectsRes.ok) setProjects(projectsData.projects || []);
      if (deptsRes.ok) setDepartments(deptsData.departments || deptsData || []);

      if (reqRes.ok && reqData.requisition) {
        const req = reqData.requisition;
        if (req.status !== "draft") {
          setErrorMessage("Can only edit draft requisitions");
          return;
        }
        setItems(
          req.items.map((i) => ({
            item: i.item?._id || i.item,
            qty: i.qty,
            purpose: i.purpose || "",
          }))
        );
        setPriority(req.priority);
        setProject(req.project?._id || req.project || "");
        setDepartment(req.department?._id || req.department || "");
        setNotes(req.notes || "");
      }
    } catch (err) {
      setErrorMessage("Failed to load requisition");
    } finally {
      setLoading(false);
    }
  };

  const addLineItem = () => {
    setItems([...items, { item: "", qty: 1, purpose: "" }]);
  };

  const removeLineItem = (index) => {
    if (items.length === 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    setItems(updated);
  };

  const handleSave = async () => {
    if (items.some((i) => !i.item || i.qty < 1)) {
      setErrorMessage("Each item must be selected with quantity >= 1");
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`/api/updateRequisition/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          items: items.map((i) => ({
            item: i.item,
            qty: parseInt(i.qty),
            purpose: i.purpose,
          })),
          priority,
          project: project || undefined,
          department: department || undefined,
          notes,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setSuccessMessage("Requisition updated successfully");
        setTimeout(() => navigate(`/Requisitions/View/${id}`), 1500);
      } else {
        setErrorMessage(data.error || "Failed to update");
        setTimeout(() => setErrorMessage(""), 3000);
      }
    } catch (err) {
      setErrorMessage("Failed to update requisition");
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="m-2 md:m-6 p-2 md:p-6">
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </div>
    );
  }

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-100">
          <IoArrowBack className="text-xl text-primary" />
        </button>
        <h2 className="text-xl font-semibold text-gray-800">Edit Requisition</h2>
      </div>

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          {successMessage}
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block font-medium mb-2 text-blue-500">Priority</label>
            <select value={priority} onChange={(e) => setPriority(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
              {PRIORITY_LEVELS.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-medium mb-2 text-blue-500">Project</label>
            <select value={project} onChange={(e) => setProject(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Select Project</option>
              {projects.map((p) => (
                <option key={p._id} value={p._id}>{p.projectCode} - {p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-medium mb-2 text-blue-500">Department</label>
            <select value={department} onChange={(e) => setDepartment(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Select Department</option>
              {departments.map((d) => (
                <option key={d._id} value={d._id}>{d.deptName}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700">Items</label>
            <button onClick={addLineItem}
              className="flex items-center gap-1 text-primary text-sm font-medium hover:underline">
              <MdAdd /> Add Item
            </button>
          </div>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Item</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Qty</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Purpose</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((lineItem, index) => (
                  <tr key={index} className="border-t">
                    <td className="px-4 py-2 text-sm">{index + 1}</td>
                    <td className="px-4 py-2">
                      <select value={lineItem.item}
                        onChange={(e) => updateLineItem(index, "item", e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm">
                        <option value="">Select item</option>
                        {inventoryItems.map((inv) => (
                          <option key={inv._id} value={inv._id}>{inv.sku} - {inv.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <input type="number" min="1" value={lineItem.qty}
                        onChange={(e) => updateLineItem(index, "qty", e.target.value)}
                        className="w-20 border border-gray-300 rounded px-2 py-1.5 text-sm" />
                    </td>
                    <td className="px-4 py-2">
                      <input type="text" placeholder="Purpose" value={lineItem.purpose}
                        onChange={(e) => updateLineItem(index, "purpose", e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                    </td>
                    <td className="px-4 py-2">
                      {items.length > 1 && (
                        <button onClick={() => removeLineItem(index)} className="text-red-500 hover:text-red-700">
                          <MdDelete />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mb-6">
          <label className="block font-medium mb-2 text-blue-500">Notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
            className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>

        <div className="flex justify-end gap-3">
          <button onClick={() => navigate(-1)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50">
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditRequisition;
